import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActionsBar from '../components/ActionsBar';
import MapsBanner from '../components/MapsBanner';
import PostCard from '../components/PostCard';
import Footer from '../components/Footer';
import FeatureShowcaseModal from '../components/FeatureShowcaseModal';
import useJsonLd from '../seo/useJsonLd';
import './HomePage.css';
import stravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import { functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { usePostHog } from 'posthog-js/react';

// Brand-new accounts (created within this window) don't get the feature
// showcase on their sign-up session; it appears on a later visit instead.
// Generous window so it never shows on the signup day, even with clock skew.
const BRAND_NEW_SUPPRESS_MS = 24 * 60 * 60 * 1000; // 24 hours

// Strava OAuth Configuration
const STRAVA_CLIENT_ID = "159733";
const STRAVA_SIGNIN_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write,profile:read_all";

// Number of feed pages to auto-load before pausing on a "Load more" button.
// The pause lets the footer become reachable while still allowing unlimited scrolling.
const MAX_AUTO_LOADS = 3;

const HomePage = ({ user, onLoginRequired, onAutoShowLoginModal }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const postRefs = useRef({});
  const loadMoreRef = useRef(null);
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDocId, setLastDocId] = useState(null);
  // Counts auto-loads since the last manual "Load more"; pauses auto-loading at MAX_AUTO_LOADS.
  const [autoLoadCount, setAutoLoadCount] = useState(0);

  // Feature showcase modal (for logged-in users who haven't seen V1)
  const [showFeatureShowcase, setShowFeatureShowcase] = useState(false);
  const featureShowcaseCheckedRef = useRef(false);

  // Template logos (styleName → logoUrl) for PostCard display
  const [templateLogos, setTemplateLogos] = useState({});


  const hydrateBadges = useCallback(async (postsToUpdate) => {
    if (!user?.uid) return;
    if (!Array.isArray(postsToUpdate) || postsToUpdate.length === 0) return;

    try {
      const getUserBadgesFunc = httpsCallable(functions, 'getUserBadges');
      const updates = await Promise.all(postsToUpdate.map(async (post) => {
        try {
          const badgesResult = await getUserBadgesFunc({ generationRequestId: post.id });
          if (badgesResult?.data?.badges && Array.isArray(badgesResult.data.badges) && badgesResult.data.badges.length > 0) {
            return {
              id: post.id,
              badges: badgesResult.data.badges
            };
          }
          return null;
        } catch (error) {
          console.log(`[HomePage] Error fetching badges for ${post.id}:`, error.message);
          return null;
        }
      }));

      const validUpdates = updates.filter(Boolean);
      if (validUpdates.length > 0) {
        setPosts(prev => prev.map(existing => {
          const update = validUpdates.find(u => u.id === existing.id);
          if (!update) return existing;
          return {
            ...existing,
            badges: update.badges
          };
        }));
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  }, [user?.uid]);

  // Load public maps from gallery
  const loadPublicMaps = async (loadMore = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const getPublicMapsFunc = httpsCallable(functions, 'getPublicMaps');
      const result = await getPublicMapsFunc({
        lastDocId: loadMore ? lastDocId : null,
        limit: 10
      });
      
      const toDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value?.toDate === 'function') return value.toDate();
        if (typeof value === 'number') return new Date(value);
        if (typeof value === 'string') {
          const parsed = Date.parse(value);
          return Number.isNaN(parsed) ? null : new Date(parsed);
        }
        if (typeof value === 'object' && value._seconds !== undefined) {
          const seconds = Number(value._seconds) || 0;
          const nanos = Number(value._nanoseconds || value.nanoseconds || 0);
          return new Date(seconds * 1000 + Math.floor(nanos / 1_000_000));
        }
        return null;
      };

      const transformedPosts = await Promise.all(result.data.maps.map(async (map) => {
        // Use user data from feed if available (performance optimization)
        let userData = {
          username: map.userDisplayName || map.username || 'user',
          avatar: map.userAvatar || '/default-avatar.png'
        };

        // Only fetch from Firestore if not provided by backend (backward compatibility)
        if ((!map.userDisplayName || !map.userAvatar) && map.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', map.userId));
            if (userDoc.exists()) {
              const data = userDoc.data();
              userData = {
                username: data.displayName || 'user',
                avatar: data.photoURL || userData.avatar
              };
            }
          } catch (err) {
            console.error('Error loading user data:', err);
          }
        }

        // Use feed badges initially - will be updated async below
        const awardedBadges = map.badges || [];

        return {
          id: map.id,
          userId: map.userId,
          user: {
            id: map.userId,
            username: userData.username,
            avatar: userData.avatar
          },
          images: [map.generatedImageUrl, map.originalImageUrl].filter(Boolean),
          media: [
            map.generatedImageUrl ? { kind: 'image', uri: map.generatedImageUrl, role: 'generated' } : null,
            map.videoUrl ? { kind: 'video', uri: map.videoUrl, role: 'video', orientation: map.videoOrientation } : null,
            map.originalImageUrl ? { kind: 'image', uri: map.originalImageUrl, role: 'original' } : null,
          ].filter(Boolean),
          caption: map.activityName || 'Mythic Adventure',
          activityType: map.activityType?.toLowerCase() || 'ride',
          stravaActivityId: map.stravaId,
          stravaStats: map.stravaStats || null,
          likes: map.likeCount || 0,
          createdAt: toDate(map.sharedAt) || toDate(map.createdAt) || new Date(),
          location: '', // TODO: Get location from map data
          isLiked: false,
          isSaved: false,
          badges: awardedBadges,
          mapOrientation: map.mapOrientation || 'landscape',
          styleName: map.style || map.styleName || null
        };
      }));

      if (loadMore) {
        setPosts(prev => [...prev, ...transformedPosts]);
      } else {
        setPosts(transformedPosts);
      }

      // Hydrate badges in background
      if (user) {
        hydrateBadges(transformedPosts);
      }

      setHasMore(result.data.hasMore);
      setLastDocId(result.data.lastDocId);
      setError(null);
    } catch (error) {
      console.error('Error loading feed:', error);
      setError('Failed to load feed. Please try again.');
      // Fall back to showing no posts
      if (!loadMore) {
        setPosts([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Load feed on mount
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadPublicMaps();
    }
  }, []);

  // Feature showcase: show the "What's New" modal once per logged-in user
  // who hasn't seen V2 yet. Brand-new accounts are suppressed on their sign-up
  // session (see BRAND_NEW_SUPPRESS_MS) so they don't get stacked modals; the
  // showcase appears on a later visit instead.
  useEffect(() => {
    if (!user?.uid) {
      setShowFeatureShowcase(false);
      featureShowcaseCheckedRef.current = false;
      return;
    }
    // Guard against StrictMode's double-invoke: the ref ensures this body runs
    // once. We deliberately do NOT use a cancelled/cleanup flag here, because
    // under StrictMode the cleanup would cancel the only fetch the guard allows.
    if (featureShowcaseCheckedRef.current) return;
    featureShowcaseCheckedRef.current = true;

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const data = snap.exists() ? snap.data() : {};
        // Don't show to brand-new users on their sign-up session: a recent
        // createdAt (server timestamp set only at account creation) means this
        // is the first session right after signup. Robust to the OAuth redirect.
        const createdAtMs = typeof data?.createdAt?.toMillis === 'function' ? data.createdAt.toMillis() : null;
        if (createdAtMs !== null && (Date.now() - createdAtMs) < BRAND_NEW_SUPPRESS_MS) return;
        const seen = data?.settings?.featureShowcaseSeenV2 === true;
        if (!seen) {
          setShowFeatureShowcase(true);
          posthog?.capture('feature_showcase_viewed', { version: 'V2' });
        }
      } catch (err) {
        console.warn('[HomePage] failed to load feature showcase flags:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const persistFeatureShowcaseSeen = async () => {
    if (!user?.uid) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.featureShowcaseSeenV2': true,
      });
    } catch (err) {
      console.warn('[HomePage] failed to persist featureShowcaseSeenV2:', err);
    }
  };

  const handleFeatureShowcaseDismiss = () => {
    setShowFeatureShowcase(false);
    posthog?.capture('feature_showcase_dismissed', { version: 'V2' });
    persistFeatureShowcaseSeen();
  };

  const handleFeatureShowcaseTryItNow = () => {
    posthog?.capture('feature_showcase_cta_clicked', { version: 'V2' });
    setShowFeatureShowcase(false);
    persistFeatureShowcaseSeen();
  };

  // Auto-open the Join modal for logged-out visitors. Fires once per page mount
  // (so the modal returns on every reload, but explicit dismissal via × stays
  // dismissed until the next reload). Intentionally no sessionStorage gate.
  useEffect(() => {
    if (user) return undefined;
    if (typeof window === 'undefined') return undefined;
    const timer = setTimeout(() => {
      onAutoShowLoginModal?.();
    }, 10000);
    return () => clearTimeout(timer);
  }, [user, onAutoShowLoginModal]);

  // Load template logos for PostCard display (same pattern as CreatePage)
  useEffect(() => {
    if (!user) return;
    const loadTemplateLogos = async () => {
      try {
        const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
        const result = await getTemplatesFunc();
        if (result.data?.templates) {
          const logoMap = {};
          result.data.templates.forEach(t => {
            if (t.logoUrl) {
              let baseName = t.name;
              if (baseName.includes(' 3D')) baseName = baseName.replace(' 3D', '').trim();
              if (baseName.endsWith('3D')) baseName = baseName.slice(0, -2).trim();
              if (!logoMap[baseName]) logoMap[baseName] = t.logoUrl;
            }
          });
          setTemplateLogos(logoMap);
        }
      } catch (err) {
        console.error('Error loading template logos:', err);
      }
    };
    loadTemplateLogos();
  }, [user]);

  useEffect(() => {
    // Check if we navigated here with a post to highlight
    const postIdToHighlight = location.state?.highlightPostId;
    if (postIdToHighlight && postRefs.current[postIdToHighlight]) {
      // Scroll to the post
      setTimeout(() => {
        postRefs.current[postIdToHighlight]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        // Highlight the post temporarily
        setHighlightedPostId(postIdToHighlight);
        setTimeout(() => setHighlightedPostId(null), 2000);
      }, 100);
    }
  }, [location.state, posts]);

  // JSON-LD + title (shared source; only injects if the static HTML lacks it).
  useJsonLd('/art');

  // Infinite scroll - auto-load more posts when scrolling near bottom.
  // After MAX_AUTO_LOADS consecutive auto-loads we stop observing, so the user
  // reaches a "Load more" button (and the footer below it) instead of loading forever.
  useEffect(() => {
    if (!hasMore || isLoading || isLoadingMore || autoLoadCount >= MAX_AUTO_LOADS) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setAutoLoadCount(count => count + 1);
        loadPublicMaps(true);
      }
    }, { rootMargin: '200px 0px', threshold: 0 });

    const node = loadMoreRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [hasMore, isLoading, isLoadingMore, autoLoadCount, posts.length]);

  const handleStravaConnect = () => {
    if (!STRAVA_CLIENT_ID || STRAVA_CLIENT_ID === "YOUR_STRAVA_CLIENT_ID") {
      alert("Strava Client ID placeholder is still in use. Please update it.");
      return;
    }

    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('stravaOAuthState', state);

    const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=auto&state=${state}`;
    window.location.href = consentUrl;
  };

  // Mock data - always available
  const mockCurrentUser = user ? {
    id: user.uid,
    username: user.displayName || 'you',
    avatar: user.photoURL || '/default-avatar.png'
  } : null;

  const mockStories = [
    {
      id: '1',
      userId: '2',
      user: {
        id: '2',
        username: 'japan_cyclist',
        displayName: 'Yuki Tanaka',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      },
      image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&h=400&fit=crop',
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
      viewed: false
    }
  ];

  if (isLoading) {
    return (
      <div className="home-page">
        <ActionsBar user={user} onLoginRequired={onLoginRequired} />
        <div className="feed-container">
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b6b6b' }}>
            <div className="loading-spinner" style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e8ddd0',
              borderTop: '3px solid #8b9dc3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p>{t('homePage.loadingFeed', 'Loading feed...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-page">
        <ActionsBar user={user} onLoginRequired={onLoginRequired} />
        <div className="feed-container">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p style={{ color: '#ef4444', marginBottom: '16px' }}>{error}</p>
            <button 
              onClick={() => loadPublicMaps()} 
              style={{
                padding: '10px 20px',
                background: '#8b9dc3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              {t('gallery.tryAgain', 'Try Again')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Map Generation banner — sits above the Mythic World banner so first-time
         logged-out visitors discover the Map Generation Engine landing page. */}
      <MapsBanner />
      {/* Mythic World Early Access Banner */}
      <ActionsBar user={user} onLoginRequired={onLoginRequired} />
      
      {/* Feed */}
      <div className="feed-container">

        {posts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b6b6b' }}>
            <p style={{ fontSize: '18px', marginBottom: '8px' }}>{t('homePage.noPosts', 'No posts yet')}</p>
            <p style={{ fontSize: '14px' }}>{t('homePage.beFirst', 'Be the first to share your mythic map!')}</p>
          </div>
        ) : (
          <>
            {posts.map((post) => (
              <div 
                key={post.id}
                ref={el => postRefs.current[post.id] = el}
                className={highlightedPostId === post.id ? 'post-wrapper highlighted' : 'post-wrapper'}
              >
                <PostCard
                  post={post}
                  user={user}
                  onLoginRequired={onLoginRequired}
                  navigateOnImageClick={false}
                  templateLogos={templateLogos}
                />
              </div>
            ))}
            
            {hasMore && (
              autoLoadCount < MAX_AUTO_LOADS ? (
                <div ref={loadMoreRef} style={{ textAlign: 'center', padding: '20px' }}>
                  {isLoadingMore && (
                    <div className="loading-spinner" style={{
                      width: '32px',
                      height: '32px',
                      border: '3px solid #e8ddd0',
                      borderTop: '3px solid #8b9dc3',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto'
                    }}></div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <button
                    className="feed-load-more-btn"
                    onClick={() => { setAutoLoadCount(0); loadPublicMaps(true); }}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore
                      ? t('homePage.loadingMore', 'Loading...')
                      : t('homePage.loadMore', 'Load more')}
                  </button>
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* Feature showcase modal: announces the Zwift custom 2D world maps. */}
      <FeatureShowcaseModal
        open={showFeatureShowcase}
        onDismiss={handleFeatureShowcaseDismiss}
        onTryItNow={handleFeatureShowcaseTryItNow}
      />

      <Footer />
    </div>
  );
};

export default HomePage;
